import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from "styled-components";
import {useTable, useSortBy, useGlobalFilter, useAsyncDebounce} from 'react-table';
import {get} from "lodash";
import moment from "moment";

import {getString} from "../../utils/translations";
import {getAsset} from "../../utils";
import {REQUEST_DATE_FORMAT} from "../../constants";
import {getAdminBillingData} from "../../redux/adminBilling/api";
import colors from "../../styles/colors";
import fonts from "../../styles/fonts";
import Input from "../../components/Input";


function GlobalFilter({preGlobalFilteredRows, globalFilter, setGlobalFilter}) {
    const [value, setValue] = useState(globalFilter);
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined);
    }, 200);

    return (
        <div style={{display: "flex"}}>
            <Input
                type={"text"}
                value={value}
                onChange={(val) => {
                    setValue(val);
                    onChange(val);
                }}
                placeholder={getString("search_organization")}
                placeholderIcon={"icons/search.svg"}
                style={{width: 250}}
            />
        </div>
    );
}

const AdminBilling = ({startDate, endDate}) => {
    const loading = useSelector(state => state.adminBilling.loading);
    const organizations = useSelector(state => state.adminBilling.organizations);
    const totals = useSelector(state => state.adminBilling.totals);
    const dispatch = useDispatch();

    useEffect(() => {
        if (startDate && endDate) {
            let formattedStartDate = moment(startDate).format(REQUEST_DATE_FORMAT);
            let formattedEndDate = moment(endDate).format(REQUEST_DATE_FORMAT);
            dispatch(getAdminBillingData({startDate: formattedStartDate, endDate: formattedEndDate}));
        }
    }, [startDate, endDate, dispatch]);

    const columns = useMemo(() => [
        {
            id: "orgName",
            Header: getString("organization"),
            accessor: row => row.orgName || "—",
            Cell: ({value}) => (
                <CellText style={{fontWeight: 700}}>{value}</CellText>
            ),
        },
        {
            id: "lastUsed",
            Header: getString("last_used"),
            accessor: row => row.lastUsed,
            Cell: ({value}) => (
                <CellText>
                    {value ? moment(value).format("MMM DD, YYYY") : "—"}
                </CellText>
            ),
            sortType: (rowA, rowB) => {
                const a = rowA.original.lastUsed ? new Date(rowA.original.lastUsed) : new Date(0);
                const b = rowB.original.lastUsed ? new Date(rowB.original.lastUsed) : new Date(0);
                return a - b;
            }
        },
        {
            id: "clientApps",
            Header: getString("client_apps"),
            accessor: row => {
                const apps = row.clientApps || [];
                return apps.reduce((sum, a) => sum + (a.totalUnits || 0), 0);
            },
            Cell: ({row}) => {
                const apps = (row.original.clientApps || []).filter(a => a.totalUnits > 0);
                if (apps.length === 0) return <CellText>—</CellText>;
                return (
                    <AppBreakdown>
                        {apps.map((a, i) => (
                            <AppRow key={a.id || i}>
                                <AppName>{a.appClient || a.name || "unknown"}</AppName>
                                <AppUnits>{(a.totalUnits || 0).toLocaleString()} units</AppUnits>
                            </AppRow>
                        ))}
                    </AppBreakdown>
                );
            },
            sortType: "basic"
        },
        {
            id: "totalUnits",
            Header: getString("ai_units"),
            accessor: row => row.totalUnits || 0,
            Cell: ({value}) => (
                <CellText style={{textAlign: "center"}}>{value.toLocaleString()}</CellText>
            ),
            sortType: "basic"
        },
        {
            id: "sessionCount",
            Header: getString("sessions"),
            accessor: row => row.sessionCount || 0,
            Cell: ({value}) => (
                <CellText style={{textAlign: "center"}}>{value.toLocaleString()}</CellText>
            ),
            sortType: "basic"
        },
    ], []);

    const tableData = useMemo(() => organizations, [organizations]);

    const tableInstance = useTable(
        {
            columns,
            data: tableData,
            initialState: {
                sortBy: [{id: "totalUnits", desc: true}]
            }
        },
        useGlobalFilter,
        useSortBy
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        preGlobalFilteredRows,
        setGlobalFilter,
        state
    } = tableInstance;

    return (
        <div>
            {loading ? <div>Loading...</div> : (
                <>
                    <TotalsRow>
                        <TotalBox>
                            <TotalLabel>{getString("total_organizations")}</TotalLabel>
                            <TotalValue>{totals.totalOrgs}</TotalValue>
                        </TotalBox>
                        <TotalBox>
                            <TotalLabel>{getString("total_ai_units")}</TotalLabel>
                            <TotalValue>{(totals.totalUnits || 0).toLocaleString()}</TotalValue>
                        </TotalBox>
                        <TotalBox>
                            <TotalLabel>{getString("total_sessions")}</TotalLabel>
                            <TotalValue>{(totals.totalSessions || 0).toLocaleString()}</TotalValue>
                        </TotalBox>
                    </TotalsRow>

                    <TableContainer>
                        <TableHeader>
                            <TableTitle>{getString("organizations_usage")}</TableTitle>
                            <GlobalFilter
                                preGlobalFilteredRows={preGlobalFilteredRows}
                                globalFilter={state.globalFilter}
                                setGlobalFilter={setGlobalFilter}
                            />
                        </TableHeader>

                        <StyledTable {...getTableProps()}>
                            <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            style={{
                                                textAlign: 'center',
                                                border: 'none',
                                                color: column.isSorted ? colors.lipstick : '#909090',
                                                fontWeight: column.isSorted ? "800" : "700",
                                                fontFamily: fonts.OpenSans,
                                                fontSize: 13,
                                                letterSpacing: 1.12,
                                                cursor: "pointer",
                                                padding: "8px 12px"
                                            }}
                                        >
                                            {column.render('Header')}
                                            <SortIndicator>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? <img src={getAsset("icons/arrow_blue.svg")} alt="desc"/>
                                                        : <img style={{transform: "rotate(180deg)"}}
                                                               src={getAsset("icons/arrow_blue.svg")} alt="asc"/>
                                                    : <img src={getAsset("icons/arrow_blue.svg")} alt="sort"
                                                           style={{opacity: 0.3}}/>
                                                }
                                            </SortIndicator>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {rows.map((row, i) => {
                                prepareRow(row);
                                return (
                                    <tr className="tbl-row" {...row.getRowProps()} key={i}>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </StyledTable>

                        {rows.length === 0 && !loading && (
                            <EmptyState>{getString("no_data")}</EmptyState>
                        )}
                    </TableContainer>
                </>
            )}
        </div>
    );
};

export default AdminBilling;

const TotalsRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    margin-bottom: 25px;
`;

const TotalBox = styled.div`
    background: #FFFFFF;
    border: 1px solid rgba(46, 91, 255, 0.08);
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.15);
    border-radius: 10px;
    padding: 20px 30px;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const TotalLabel = styled.div`
    font-family: Open Sans, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #8798AD;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
`;

const TotalValue = styled.div`
    font-family: Rubik, sans-serif;
    font-size: 28px;
    font-weight: 300;
    color: #2E384D;
`;

const TableContainer = styled.div`
    background: #FFFFFF;
    border: 1px solid rgba(46, 91, 255, 0.08);
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.15);
    border-radius: 10px;
    padding: 20px;
`;

const TableHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
`;

const TableTitle = styled.div`
    font-family: Rubik, sans-serif;
    font-style: normal;
    font-weight: 300;
    font-size: 25px;
    line-height: 32px;
    color: #2E384D;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 12px;

    tbody tr {
        background: #f9f9fb;
        border-radius: 10px;
        transition: box-shadow .3s;
    }

    tbody tr:hover {
        box-shadow: 0 0 11px rgba(33, 33, 33, .15);
    }

    th, td {
        margin: 0;
        padding: 10px 12px;
    }

    td:first-child {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
    }

    td:last-child {
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
    }
`;

const CellText = styled.div`
    font-family: Open Sans, sans-serif;
    font-size: 14px;
    color: #2E384D;
    text-align: center;
`;

const AppBreakdown = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 2px 0;
`;

const AppRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const AppName = styled.span`
    font-family: Open Sans, sans-serif;
    font-size: 13px;
    color: #2E384D;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AppUnits = styled.span`
    font-family: Open Sans, sans-serif;
    font-size: 12px;
    color: #8798AD;
    white-space: nowrap;
`;

const SortIndicator = styled.div`
    display: inline-block;
    margin-left: 4px;
    vertical-align: middle;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px;
    color: #8798AD;
    font-family: Open Sans, sans-serif;
    font-size: 15px;
`;
